import { useEffect, useCallback, useRef } from 'react';
import { parametersService } from '../services';
import { useParametersStore } from '../store/parametersStore';
import type { RegionDTO, StateReferenceDTO } from '../types/parameters.types';

export const useParameters = (onError: (msg: string) => void) => {
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const regions = useParametersStore((s) => s.regions);
	const selectedRegionId = useParametersStore((s) => s.selectedRegionId);
	const stateReference = useParametersStore((s) => s.stateReference);
	const activeTab = useParametersStore((s) => s.activeTab);
	const selectedTexture = useParametersStore((s) => s.selectedTexture);
	const isLoadingRegions = useParametersStore((s) => s.isLoadingRegions);
	const isLoadingReference = useParametersStore((s) => s.isLoadingReference);
	const error = useParametersStore((s) => s.error);
	const getSelectedRegion = useParametersStore((s) => s.getSelectedRegion);

	useEffect(() => {
		const {
			setLoadingRegions,
			setRegions,
			selectRegion,
			setError,
		} = useParametersStore.getState();

		const load = async () => {
			setLoadingRegions(true);
			try {
				const data = await parametersService.fetchRegions();
				setRegions(data);
				if (data.length > 0) selectRegion(data[0]._id);
			} catch {
				const msg = 'Erro ao carregar regiões. Tente novamente.';
				setError(msg);
				onErrorRef.current(msg);
			} finally {
				setLoadingRegions(false);
			}
		};

		load();
	}, []);

	useEffect(() => {
		const selectedRegion = useParametersStore.getState().getSelectedRegion();
		if (!selectedRegion) return;

		const { setLoadingReference, setStateReference, setError } =
			useParametersStore.getState();

		const load = async () => {
			setLoadingReference(true);
			try {
				const data = await parametersService.fetchStateReference(
					selectedRegion.stateReferenceId
				);
				setStateReference(data);
			} catch {
				const msg = 'Erro ao carregar parâmetros da região selecionada.';
				setError(msg);
				onErrorRef.current(msg);
			} finally {
				setLoadingReference(false);
			}
		};

		load();
	}, [selectedRegionId]);

	const setSelectedRegion = useCallback((region: RegionDTO) => {
		useParametersStore.getState().selectRegion(region._id);
	}, []);

	const setActiveTab = useParametersStore((s) => s.setActiveTab);
	const setSelectedTexture = useParametersStore((s) => s.setSelectedTexture);

	const updateStateReference = useCallback(async (updated: StateReferenceDTO) => {
		const result = await parametersService.updateStateReference(updated._id, updated);
		useParametersStore.getState().setStateReference(result);
	}, []);

	const createRegion = useCallback((name: string, stateCodes: string[]) => {
		const newRegion: RegionDTO = {
			_id: `region-${Date.now()}`,
			name,
			stateCodes,
			stateReferenceId: 'sr-go',
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};
		useParametersStore.getState().addRegion(newRegion);
	}, []);

	const updateRegion = useCallback((regionId: string, name: string, stateCodes: string[]) => {
		useParametersStore.getState().updateRegion(regionId, name, stateCodes);
	}, []);

	return {
		regions,
		selectedRegion: getSelectedRegion() ?? null,
		stateReference,
		activeTab,
		selectedTexture,
		isLoadingRegions,
		isLoadingReference,
		error,
		setSelectedRegion,
		setActiveTab,
		setSelectedTexture,
		updateStateReference,
		createRegion,
		updateRegion,
	};
};
